import { ApiError } from 'api/http/types';
import { useCallback } from 'react';

import {
  paymentApi,
  westpacApi,
  monthlyinstalmentApi,
  bindApi,
} from 'scenes/common/api';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';
import {
  isApiError,
  isBusinessError as isBusinessErrorData,
  isPaymentError,
  makeApiError,
  makeApiReloading,
  makeApiSuccess,
  makePaymentError,
} from 'scenes/common/helpers/apiData';
import {
  ApiData,
  BusinessErrorDetail,
  CreditCardDetails,
  DirectDebitDetails,
  InsuranceName,
  PaymentMethod,
} from 'scenes/common/types';
import {
  setCardType,
  setPaymentDate,
  setPaymentMethod,
} from 'scenes/landlord/actions';
import { getQuoteNumber, getQuoteReference } from 'scenes/landlord/selectors';
import { isBusinessError } from 'scenes/common/api/quote';

import {
  LandlordApiResponses,
  LandlordFormAnswer,
  LandlordFormState,
  LandlordFunnelState,
  LandlordQuoteDetails,
  LandlordStepType,
  ReferenceData,
} from 'scenes/landlord/types';
import { unmarshalReceiptNumber } from 'scenes/common/api/quote/marshaller/payment';
import { handleError } from 'scenes/landlord/api/quote/services/quote';

const handlePaymentError = (
  err: ApiError<BusinessErrorDetail>,
  funnelState: LandlordFunnelState
): ApiData<LandlordQuoteDetails> =>
  isBusinessError(err)
    ? makePaymentError(err.message, funnelState.apiResponses.quoteDetails)
    : makeApiError(err.message, funnelState.apiResponses.quoteDetails);

export const usePaymentActions = (
  handleAnswer: (f: LandlordFormAnswer) => void,
  onNextIfLocationIs: (s: LandlordStepType, values?: LandlordFormState) => void,
  onApiResponse: (data: Partial<LandlordApiResponses>) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<LandlordFunnelState>();

  const handleQuoteResponse = useCallback(
    (quoteDetails) => {
      onApiResponse({
        quoteDetails,
      });
      return quoteDetails;
    },
    [onApiResponse]
  );

  const onUpdatePaymentDate = useCallback(
    (paymentDate: string) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      monthlyinstalmentApi()
        .recalculateMonthlyInstalment(
          InsuranceName.Landlord,
          getQuoteNumber(funnelState),
          getQuoteReference(funnelState),
          paymentDate
        )
        .then((recalculatedInstalments) =>
          makeApiSuccess({
            ...funnelState.apiResponses.quoteDetails.data,
            recalculatedInstalments,
          })
        )
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessErrorData(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setPaymentDate(paymentDate));
          }
        })
        .catch((err) =>
          handleQuoteResponse(handleError(err, funnelState, referenceData))
        );
    },
    [
      funnelState,
      handleAnswer,
      handleQuoteResponse,
      onApiResponse,
      referenceData,
    ]
  );

  const onBpayPayment = useCallback(async () => {
    onApiResponse({
      quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
    });
    try {
      const quoteDetails = await bindApi().bindBpayPayment(
        InsuranceName.Landlord,
        getQuoteNumber(funnelState),
        getQuoteReference(funnelState),
        funnelState.paymentDetails
      );
      handleQuoteResponse(
        makeApiSuccess(funnelState.apiResponses.quoteDetails.data)
      );
      if (
        !isPaymentError(quoteDetails) &&
        !isBusinessErrorData(quoteDetails) &&
        !isApiError(quoteDetails)
      ) {
        handleAnswer(setPaymentMethod(PaymentMethod.BPAY));
        onNextIfLocationIs(funnelState.step);
      }
    } catch (err) {
      handleQuoteResponse(handlePaymentError(err, funnelState));
    }
  }, [
    funnelState,
    handleAnswer,
    handleQuoteResponse,
    onApiResponse,
    onNextIfLocationIs,
  ]);

  const onDirectDebitPayment = useCallback(
    async (directDebitDetails: DirectDebitDetails) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      try {
        const quoteDetails = await bindApi().bindDirectDebitPayment(
          InsuranceName.Landlord,
          getQuoteNumber(funnelState),
          getQuoteReference(funnelState),
          funnelState.paymentDetails,
          directDebitDetails
        );
        handleQuoteResponse(
          makeApiSuccess(funnelState.apiResponses.quoteDetails.data)
        );
        if (
          !isPaymentError(quoteDetails) &&
          !isBusinessErrorData(quoteDetails) &&
          !isApiError(quoteDetails)
        ) {
          onNextIfLocationIs(funnelState.step);
        }
      } catch (err) {
        handleQuoteResponse(handlePaymentError(err, funnelState));
      }
    },
    [funnelState, handleQuoteResponse, onApiResponse, onNextIfLocationIs]
  );

  const onCreditCardPayment = useCallback(
    async (creditCardDetails: CreditCardDetails) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      try {
        let paymentApiData: { supplierBusinessCode: string; token: string };
        let westpacApiData: { singleUseTokenId: string };
        try {
          paymentApiData = await paymentApi().getToken();
        } catch (err) {
          throw new Error(`Payment gateway: ${err.message}`);
        }
        try {
          westpacApiData = await westpacApi().getSingleUseToken(
            paymentApiData.supplierBusinessCode,
            atob(paymentApiData.token),
            creditCardDetails
          );
        } catch (err) {
          throw new Error(`Westpac api: ${err.message}`);
        }
        const quoteDetails = await bindApi().bindCreditCardPayment(
          InsuranceName.Landlord,
          getQuoteNumber(funnelState),
          getQuoteReference(funnelState),
          funnelState.paymentDetails,
          westpacApiData.singleUseTokenId
        );
        handleQuoteResponse(
          makeApiSuccess({
            ...funnelState.apiResponses.quoteDetails.data,
            receiptNumber: unmarshalReceiptNumber(quoteDetails.payload),
          })
        );
        if (
          !isPaymentError(quoteDetails) &&
          !isBusinessErrorData(quoteDetails) &&
          !isApiError(quoteDetails)
        ) {
          handleAnswer(setCardType(creditCardDetails.cardType));
          onNextIfLocationIs(funnelState.step);
        }
      } catch (err) {
        handleQuoteResponse(handlePaymentError(err, funnelState));
      }
    },
    [
      funnelState,
      handleAnswer,
      handleQuoteResponse,
      onApiResponse,
      onNextIfLocationIs,
    ]
  );

  return {
    onUpdatePaymentDate,
    onBpayPayment,
    onDirectDebitPayment,
    onCreditCardPayment,
  };
};
